import React, { useState } from 'react';
import { ChevronRightIcon, QuestionMarkCircleIcon } from '@heroicons/react/24/outline';

/**
 * NonInteractiveGuidance — collapsed-by-default explainer for the non-interactive
 * sign-in panel.
 *
 * COLLAPSED BY DEFAULT, DELIBERATELY. This sits on the IP Addresses tab of every case,
 * and an always-open block of guidance competes with the coverage and failure
 * disclosures directly above it — which report real, case-specific problems and must
 * stay the loudest thing in that column. Guidance is read once and then never again;
 * a disclosure is read every time. They must not look alike.
 *
 * The content is ordered to match the panel's own layout (device → session → adjacency →
 * volume → network → cautions), so reading this top-to-bottom walks an expanded row
 * top-to-bottom. If either order changes, change both.
 *
 * Every claim here is one the code can actually support. In particular it does NOT say
 * a shared session proves attribution, does NOT say a scripted user agent is a verdict,
 * and does NOT promise timeline rows — all three were wrong beliefs held during this
 * feature's construction, and each cost a round of rework.
 */

function Item({ n, title, children, tone = 'gray' }) {
  const dot = tone === 'red' ? 'bg-red-400' : tone === 'amber' ? 'bg-amber-400' : 'bg-sky-400';
  return (
    <div className="flex gap-2">
      <span className={`mt-1.5 h-1.5 w-1.5 rounded-full shrink-0 ${dot}`} />
      <div className="min-w-0">
        <p className="text-xs text-gray-300 font-medium">
          {n != null && <span className="text-gray-500 mr-1">{n}.</span>}{title}
        </p>
        <p className="text-[11px] text-gray-500 leading-relaxed">{children}</p>
      </div>
    </div>
  );
}

export default function NonInteractiveGuidance() {
  const [open, setOpen] = useState(false);

  return (
    <div className="mb-3">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-1 text-[11px] text-sky-400 hover:text-sky-300"
      >
        <QuestionMarkCircleIcon className="h-3.5 w-3.5" />
        When and how to evaluate non-interactive sign-ins
        <ChevronRightIcon className={`h-3 w-3 transition-transform ${open ? 'rotate-90' : ''}`} />
      </button>

      {open && (
        <div className="mt-2 rounded-lg border border-gray-800 bg-gray-900/40 p-3 space-y-3">
          <div>
            <p className="text-xs font-semibold text-gray-300 mb-1">What this is</p>
            <p className="text-[11px] text-gray-500 leading-relaxed">
              Microsoft records a <span className="text-gray-400">non-interactive</span> sign-in whenever
              an already-issued token is presented — no password, no MFA prompt. It is a separate log
              that the investigation does not read, so an IP or device used <span className="italic">only</span>{' '}
              to replay a stolen token leaves no trace anywhere else on this case. This button queries
              that log for this user and window and adds what it finds to the table below.
            </p>
          </div>

          <div>
            <p className="text-xs font-semibold text-gray-300 mb-1">When to run it</p>
            <ul className="text-[11px] text-gray-500 leading-relaxed list-disc pl-4 space-y-0.5">
              <li>Any case with a phishing or AiTM entry point — token replay is the dominant follow-on.</li>
              <li>Before closing a case, so the IOC list you hand over is not partial.</li>
              <li>After a password reset or token revoke, to check whether access actually stopped.</li>
              <li>When activity appears with no matching sign-in, or a session seems to resume from nowhere.</li>
            </ul>
          </div>

          <div>
            <p className="text-xs font-semibold text-amber-300 mb-1">What it will not tell you</p>
            <p className="text-[11px] text-gray-500 leading-relaxed">
              <span className="text-gray-400">Nothing about what was done.</span> This log records
              authentication only. The IPs it recovers have no rows in the case timeline and never will —
              mail read, rules created and files touched all live in the timeline, under whatever IP was
              recorded on that event. Do not expect new timeline entries after running this.
            </p>
          </div>

          <div>
            <p className="text-xs font-semibold text-gray-300 mb-2">How to judge a row, in order</p>
            <div className="space-y-2">
              <Item n={1} title="Device first" tone="red">
                The strongest single signal. A named device you recognise from the case is almost always
                the user. Records carrying <span className="text-gray-400">no device claim at all</span> —
                especially most of them — are token reuse, which is the thing this log exists to show.
              </Item>
              <Item n={2} title="Session" tone="red">
                Does it share a session with IPs you have already marked malicious, or with recorded
                actions? That is the link nothing else on the case can make.{' '}
                <span className="text-amber-300">It is corroboration, not proof</span> — a stolen token
                keeps the victim&apos;s own session id, so the same session legitimately carries their
                real activity too. Judge each event on the IP recorded against it.
              </Item>
              <Item n={3} title="Neighbours" tone="amber">
                A badge saying other IPs in the same /24 or network are already classified malicious.
                Attacker infrastructure clusters; a lone address in a hostile block rarely is one.
              </Item>
              <Item n={4} title="Timing" tone="amber">
                Activity starting before your first known indicator means the intrusion predates what you
                knew when you opened the case. For devices, <span className="text-gray-400">registered
                during this case</span> means it did not exist before the incident.
              </Item>
              <Item n={5} title="Outcome, after a revoke" tone="amber">
                <span className="font-mono text-gray-400">50173</span> means the token grant was revoked.
                The real user re-authenticates and goes on succeeding; a stolen token can only keep
                failing. Compare <span className="text-gray-400">last success</span> across IPs — an
                address that stopped succeeding at the reset and one still working today are telling you
                different things.
              </Item>
              <Item n={6} title="Network, last">
                Hosting providers are attacker-characteristic; business, residential and wireless usually
                are not. <span className="text-amber-300">But &ldquo;unenriched&rdquo; means never scored,
                not clean</span> — several confirmed attacker IPs on real cases have no reputation record
                at all, which is exactly how they stay hidden from reputation-based hunting.
              </Item>
            </div>
          </div>

          <div>
            <p className="text-xs font-semibold text-amber-300 mb-1">Do not conclude from these alone</p>
            <ul className="text-[11px] text-gray-500 leading-relaxed list-disc pl-4 space-y-0.5">
              <li>
                <span className="text-gray-400">A scripted client</span> (python-requests, node-fetch).
                Automation is common on legitimate traffic — on a real case the victim&apos;s own office
                IP carried one, and an estate-wide sweep for the pattern returned a responder&apos;s own
                tooling.
              </li>
              <li><span className="text-gray-400">A browser or OS name</span> on its own. It identifies nothing.</li>
              <li><span className="text-gray-400">A shared session id</span> on its own — see above.</li>
              <li>
                <span className="text-gray-400">A quiet period.</span> Absence of failures may mean they
                were not collected for that window; the panel says so when that applies.
              </li>
            </ul>
          </div>

          <div>
            <p className="text-xs font-semibold text-gray-300 mb-1">When you have decided</p>
            <p className="text-[11px] text-gray-500 leading-relaxed">
              Classify the IP like any other — it then colours across the timeline, devices and reports,
              and joins the case&apos;s IOC list. Confirmed attacker addresses can be promoted to the
              global blocklist from the toolbar. A device you did not know about is a containment action:
              its tokens need revoking and its registration reviewing, not just a note in the report.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
